import { MAX_HEIGHT_FRACTION, MAX_WIDTH_FRACTION, RULER_PAGE_PX_PER_MODEL_INCH } from './viewport-constants';

export type ContentExtent = {
	left: number;
	right: number;
	top: number;
	bottom: number;
};

/** Guards against a degenerate extent driving the scale to Infinity. */
const MIN_EXTENT_INCHES = 1;

export const boxToExtent = (box: { x: number; y: number; width: number; height: number }): ContentExtent => {
	return {
		left: box.x,
		right: box.x + box.width,
		top: box.y,
		bottom: box.y + box.height
	};
};

export type ComputeFittedViewBoxParams = {
	/**
	 * Exact model geometry from the domain model -- never a measurement. Its size
	 * floors the fit so a partial measurement cannot inflate the scale; its
	 * position is used only until the ink lands, because the rendered ink is the
	 * authority on where the drawing actually sits.
	 */
	geometry: ContentExtent;
	/** Measured ink of the rendered annotations, or null before measurement lands. */
	ink: ContentExtent | null;
	containerPx: { w: number; h: number };
};

/**
 * Two-stage page fit.
 *
 * Stage one is arithmetic: the drawing is laid out at the ruler scale
 * (`RULER_PAGE_PX_PER_MODEL_INCH`) against exact domain-model geometry. Stage two
 * is visual: the measured ink of the dimension labels, centre markers and
 * breakpoint values takes over, and if it no longer fits the container the scale
 * is reduced uniformly until it does. Ratio is always preserved, and the result
 * sits as close to the binding edge as it can without crossing it.
 *
 * Once stage two engages the page is no longer ruler-true -- that is deliberate.
 * A page that does not fit has already lost its scale, so showing all the
 * information wins over honouring a ratio that is broken either way.
 * @param params exact geometry, measured ink, and the container's pixel size
 */
export const computeFittedViewBox = (params: ComputeFittedViewBoxParams): string => {
	const { geometry, ink, containerPx } = params;

	const content = ink ?? geometry;
	const contentWidth = Math.max(content.right - content.left, geometry.right - geometry.left, MIN_EXTENT_INCHES);
	const contentHeight = Math.max(content.bottom - content.top, geometry.bottom - geometry.top, MIN_EXTENT_INCHES);

	const widthFitScale = (MAX_WIDTH_FRACTION * containerPx.w) / contentWidth;
	const heightFitScale = (MAX_HEIGHT_FRACTION * containerPx.h) / contentHeight;
	const scale = Math.min(RULER_PAGE_PX_PER_MODEL_INCH, widthFitScale, heightFitScale);

	const vbW = containerPx.w / scale;
	const vbH = containerPx.h / scale;
	const cx = (content.left + content.right) / 2;
	const cy = (content.top + content.bottom) / 2;

	return `${cx - (vbW / 2)} ${cy - (vbH / 2)} ${vbW} ${vbH}`;
};
